import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { currentUser, authState } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Check if user UID exists in the 'admins' collection
        const adminDocRef = doc(db, 'admins', currentUser.uid);
        const adminDoc = await getDoc(adminDocRef);
        
        setIsAdmin(adminDoc.exists());
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
      
      setLoading(false);
    };

    if (authState.isAuthenticated && currentUser) {
      checkAdminStatus();
    } else if (!authState.loading) {
      setLoading(false);
    }
  }, [currentUser, authState]);

  if (authState.loading || loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <div className="loading loading-spinner loading-lg"></div>
        <p className="text-sm text-base-content/70 mt-4">
          Checking permissions...
        </p>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isAdmin === false) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh] text-center p-6">
        <h1 className="text-3xl font-bold text-red-600 mb-4">
          Access Denied
        </h1>
        <p className="text-base-content/70">
          You don't have permission to access this page.
        </p>
      </div>
    );
  }

  return isAdmin ? <>{children}</> : null;
};

export default AdminRoute; 